import { Badge, Button, SaveHint, Select } from '../../components/ui';
import type { SaveState } from '../../components/ui';
import { capturesFor, offerable, originLabel, originMismatch, originsFor } from './leadFields';
import type { ReactNode } from 'react';
import type { LeadCaptureMethod, LeadCategory, LeadSource } from './leadFields';

/**
 * Direction and Categories — the two questions every lead answers, asked the
 * same way wherever a lead is created or edited.
 *
 * Was three. Lead source came off every form in batch 49: in six batches no
 * weekly question needed it, and a field nobody reads is a field somebody
 * still has to skip past. The column and its data are untouched, so it can
 * come back without a migration if a real question for it appears.
 *
 * One component rather than three copies across New Lead, Import and the lead
 * panel. The Tracker's existing forms each grew their own field markup, which
 * is why the New Lead drawer collected five fields and the Import drawer
 * collected six of a different five.
 *
 * ── Direction is asked, never defaulted ────────────────────────────────────
 *
 * Inbound is a consent record: once set, the database refuses to change it on
 * any ordinary path. A form that pre-selected it would be putting a claim
 * about consent in somebody's mouth on every lead they create, and the only
 * way back is a TAP admin with a written reason. So the blank option stays,
 * and it says what it means.
 */

export function DirectionField({
  value,
  onChange,
  disabled,
}: {
  value: 'inbound' | 'outbound' | null;
  onChange: (next: 'inbound' | 'outbound' | null) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <Select
        label="Lead Source"
        hint="Who moved first. Lead Origin below says which channel."
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next === '' ? null : (next as 'inbound' | 'outbound'));
        }}
        options={[
          { value: '', label: 'Not said' },
          { value: 'inbound', label: 'Inbound — they came to us' },
          { value: 'outbound', label: 'Outbound — we went to them' },
        ]}
      />
      {value !== 'inbound' ? null : (
        <p className="tracker-field-note">
          Inbound is a consent record. Once saved it cannot be changed here — only a TAP admin can
          correct it, with a reason.
        </p>
      )}
    </div>
  );
}

/**
 * Lead Origin — the specific channel, scoped to the side chosen above.
 *
 * ── The naming reads backwards, and that is deliberate ─────────────────────
 *
 * On screen: "Lead Source" is Inbound/Outbound, "Lead Origin" is the channel.
 * In the database: `leads.source` holds Inbound/Outbound, `leads.lead_source_id`
 * holds the channel. So the column called source is the one labelled Source,
 * and the column called lead_source_id is the one labelled Origin.
 *
 * Kyu chose these labels knowing that. The columns were not renamed: renaming
 * one that 787 leads and a consent guard depend on, to fix a word, is a bad
 * trade. See migration 20261202000100.
 *
 * Off the forms since batch 49, which removed it for having no weekly question
 * behind it. Alex named the question — "how are LinkedIn Ads leads doing" — so
 * it returns, and it returns for free because that batch kept the column and
 * every stored value.
 */
export function OriginField({
  value,
  leadSource,
  sources,
  onChange,
  saveHint,
  disabled,
  labelHidden = false,
}: {
  value: string | null;
  /** The Inbound/Outbound answer above; scopes what is offered. */
  leadSource: 'inbound' | 'outbound' | null;
  sources: LeadSource[];
  onChange: (next: string | null) => void;
  /** So the field can say it saved, like every other one in the drawer. */
  saveHint?: ReactNode;
  disabled?: boolean;
  /** The section title already says "Lead Origin"; do not say it twice. */
  labelHidden?: boolean;
}) {
  const options = originsFor(sources, leadSource, value);
  if (options.length === 0) {
    return null;
  }
  const mismatched = originMismatch(sources, leadSource, value);

  // The Select IS the root, with nothing around it. It used to sit inside a
  // bare <div>, and that one wrapper undid the whole alignment scheme in
  // .lead-panel-two-col: the shared label/control/hint rows are handed to a
  // `> .ui-field` child, and a div is not one, so the origin control sat in
  // the label row while Capture method's control sat a row lower. Measured
  // live on Brooke Haskell, 3 Sep: origin at 447px, capture at 499px. The
  // mismatch note now travels in the hint, which is the third shared row.
  return (
    <Select
      label="Lead Origin"
      labelHidden={labelHidden}
      hint={
        <>
          {leadSource === null ? 'Pick a Lead Source above to narrow the list. ' : null}
          {!mismatched
            ? null
            : 'This origin belongs to the other side. It has been kept rather than cleared — change it if it is wrong, or leave it if the lead really came that way. '}
          {saveHint}
        </>
      }
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => {
        const next = event.target.value;
        onChange(next === '' ? null : next);
      }}
      options={[
        { value: '', label: 'Not said' },
        ...options.map((row) => ({ value: row.id, label: originLabel(row) })),
      ]}
    />
  );
}

/**
 * How their details reached us — the other half of "where did this lead come
 * from", and a separate question from it.
 *
 * Alex: "Social media > Instagram" is the source, "ManyChat DM" is the capture
 * method. Rendered directly beneath Lead Origin rather than in a section of
 * its own, because the two answers are read together and a twelfth drawer
 * section for one select would be worse than the clutter it avoided.
 *
 * Blank on all 690 existing leads by design — nothing was backfilled, because
 * the only value available to guess from is the origin, and "Typeform means
 * the website form" is a guess wearing a fact's clothes.
 */
export function CaptureMethodField({
  value,
  leadSource,
  methods,
  onChange,
  saveHint,
  disabled,
}: {
  value: string | null;
  /** The Inbound/Outbound answer; scopes what is offered, as it does for origin. */
  leadSource: 'inbound' | 'outbound' | null;
  methods: LeadCaptureMethod[];
  onChange: (next: string | null) => void;
  saveHint?: ReactNode;
  disabled?: boolean;
}) {
  const options = capturesFor(methods, leadSource, value);
  if (options.length === 0) {
    return null;
  }

  return (
    <Select
      label="Capture method"
      hint={saveHint}
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => {
        const next = event.target.value;
        onChange(next === '' ? null : next);
      }}
      options={[
        { value: '', label: 'Not said' },
        ...options.map((row) => ({ value: row.id, label: String(row.name) })),
      ]}
    />
  );
}

/**
 * Categories, multi-select, as toggles rather than a multi-select box.
 *
 * There are two of them today and there will be a handful ever. A native
 * multi-select would hide the second one behind a scroll and a modifier key;
 * two chips show both and take one click.
 */
export function CategoryField({
  selected,
  onChange,
  categories,
  hint,
  labelHidden = false,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  categories: LeadCategory[];
  hint?: string;
  /** Inside a titled section the title is the label and the hint mark is the help. */
  labelHidden?: boolean;
}) {
  const help = hint ?? 'What this opportunity is about. A lead can be more than one.';
  const live = offerable(categories);
  if (live.length === 0) {
    return null;
  }
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="tracker-field-block">
      {labelHidden ? null : <span className="tracker-field-label">Categories</span>}
      <div className="lead-filter-chips">
        {live.map((category) => (
          <button
            key={category.id}
            type="button"
            className={
              selected.includes(category.id)
                ? 'lead-filter-chip lead-filter-chip--on'
                : 'lead-filter-chip'
            }
            aria-pressed={selected.includes(category.id)}
            onClick={() => toggle(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>
      {labelHidden ? null : <p className="tracker-field-note">{help}</p>}
    </div>
  );
}

/**
 * The nurture switch and its note.
 *
 * Was a switch, a since-date and a note. The date was read by nothing but its
 * own badge, and it was null for every lead predating the restructure — so the
 * screen carried a paragraph explaining why a field was empty, which is the
 * shape of a field that has not earned its place. The timeline already records
 * when nurture was turned on and by whom, which is the better record of the
 * same fact. Batch 49.
 *
 * Kyu: "Nurture is a relationship with the person/company, not a particular
 * Deal." Turning it off clears the note, because a lead not in nurture should
 * not carry a nurture story.
 */
export function NurturePanel({
  active,
  note,
  onToggle,
  onNoteChange,
  onNoteBlur,
  noteSave,
  pending,
  labelHidden = false,
}: {
  active: boolean;
  note: string;
  onToggle: (next: boolean) => void;
  onNoteChange: (next: string) => void;
  /** Write anything still pending. The drawer can close without a blur. */
  onNoteBlur: () => void;
  /** So the note can say it saved, like every other field in the drawer. */
  noteSave: { state: SaveState; error: string };
  pending?: boolean;
  /** The section title already says "Nurture"; the row keeps only the Active badge. */
  labelHidden?: boolean;
}) {
  return (
    <div className="tracker-field-block">
      <div className="tracker-field-row">
        {labelHidden ? null : <span className="tracker-field-label">Nurture</span>}
        {!active ? null : <Badge tone="brand">Active</Badge>}
        {!active ? null : <SaveHint state={noteSave.state} error={noteSave.error} />}
      </div>
      {!active ? (
        labelHidden ? (
          <p className="tracker-field-note">Not in nurture.</p>
        ) : (
          <p className="tracker-field-note">
            Not in nurture. Nurture does not replace the status — a lead stays New, Contacted or
            Engaged while it is in nurture. It warns before direct sales follow-ups. It does not
            pause any task or stop anybody emailing.
          </p>
        )
      ) : (
        <textarea
          className="ui-textarea"
          rows={2}
          aria-label="Nurture note"
          placeholder="Why, in one line — e.g. receiving impact stories; avoid direct chase unless they re-engage"
          value={note}
          onChange={(event) => {
            onNoteChange(event.target.value);
          }}
          onBlur={onNoteBlur}
        />
      )}
      <Button
        variant={labelHidden ? 'ghost' : 'secondary'}
        size="sm"
        disabled={pending}
        onClick={() => onToggle(!active)}
      >
        {active ? 'Take out of nurture' : 'Put in nurture'}
      </Button>
    </div>
  );
}
